// TaskList.js - FILTROS CORREGIDOS
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Visibility,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useAuth } from "../../hooks/useAuth";
import {
  TASK_STATUS,
  TASK_STATUS_LABELS,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
} from "../../utils/models/taskModel";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";

const TaskList = ({ onEdit, onView, onRefresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const { data: tasks, loading } = useCollection("tasks");
  const { update, remove } = useAuditFirestore("tasks");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(TASK_STATUS.PENDING);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("mine");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter((task) => {
      const matchesSearch =
        search === "" ||
        task.title?.toLowerCase().includes(search.toLowerCase()) ||
        task.description?.toLowerCase().includes(search.toLowerCase()) ||
        task.assignedToName?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || task.status === statusFilter;
      const matchesPriority =
        priorityFilter === "all" || task.priority === priorityFilter;
      const matchesAssigned =
        assignedFilter === "all" ||
        (assignedFilter === "mine" &&
          ((Array.isArray(task.assignedTo) &&
            task.assignedTo.includes(user.uid)) ||
            (typeof task.assignedTo === "string" &&
              task.assignedTo === user.uid))) ||
        (assignedFilter === "unassigned" &&
          (!task.assignedTo ||
            (Array.isArray(task.assignedTo) && task.assignedTo.length === 0) ||
            (typeof task.assignedTo === "string" && task.assignedTo === "")));
      return (
        task.isActive !== false &&
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesAssigned
      );
    });
  }, [tasks, search, statusFilter, priorityFilter, assignedFilter, user.uid]);

  const paginatedTasks = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusToggle = async (task) => {
    const newStatus =
      task.status === TASK_STATUS.COMPLETED
        ? TASK_STATUS.PENDING
        : TASK_STATUS.COMPLETED;
    await update(task.id, {
      status: newStatus,
      completedAt:
        newStatus === TASK_STATUS.COMPLETED ? new Date().toISOString() : null,
      updatedAt: new Date().toISOString(),
    });
    onRefresh();
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === TASK_STATUS.COMPLETED) return false;
    return new Date(task.dueDate) < new Date();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      {/* Filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={1.5}>
          {/* Barra de búsqueda */}
          <TextField
            placeholder="Buscar tareas..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {/* Filtros en grid responsive */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(3, 1fr)",
                sm: "repeat(4, 1fr)",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Estado
              </InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                <MenuItem
                  value="all"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                >
                  Todos
                </MenuItem>
                {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Prioridad
              </InputLabel>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                label="Prioridad"
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                <MenuItem
                  value="all"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                >
                  Todas
                </MenuItem>
                {Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Asignación
              </InputLabel>
              <Select
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
                label="Asignación"
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              >
                <MenuItem
                  value="all"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                >
                  Todas
                </MenuItem>
                <MenuItem
                  value="mine"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                >
                  Mis tareas
                </MenuItem>
                <MenuItem
                  value="unassigned"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                >
                  Sin asignar
                </MenuItem>
              </Select>
            </FormControl>

            {/* Contador + Refrescar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                height: 44,
              }}
            >
              <Chip
                label={`${filtered.length}`}
                sx={{
                  height: 32,
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  background: theme.palette.background.paper,
                  border: "1px solid",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }}
              />
              <Tooltip title="Refrescar">
                <IconButton
                  onClick={onRefresh}
                  size="small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Stack>
      </Paper>

      {filtered.length === 0 ? (
        <EmptyState
          title={
            search || statusFilter !== "all" || priorityFilter !== "all"
              ? "Sin resultados"
              : "Sin tareas"
          }
          description={
            search || statusFilter !== "all" || priorityFilter !== "all"
              ? "Prueba con otros filtros"
              : "Crea tu primera tarea"
          }
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Tarea
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Prioridad
                  </TableCell>
                  {!isMobile && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: "0.85rem",
                      }}
                    >
                      Asignado a
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Vencimiento
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Estado
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTasks.map((task) => (
                  <TableRow
                    key={task.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      opacity: task.status === TASK_STATUS.COMPLETED ? 0.7 : 1,
                      backgroundColor: isOverdue(task)
                        ? "rgba(211, 47, 47, 0.05)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0, 95, 101, 0.02)",
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={task.status === TASK_STATUS.COMPLETED}
                        onChange={() => handleStatusToggle(task)}
                        onClick={(e) => e.stopPropagation()}
                        icon={<RadioButtonUnchecked />}
                        checkedIcon={<CheckCircle color="success" />}
                        size="small"
                      />
                    </TableCell>
                    <TableCell onClick={() => onView(task)}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          textDecoration:
                            task.status === TASK_STATUS.COMPLETED
                              ? "line-through"
                              : "none",
                          fontSize: { xs: "0.75rem", sm: "0.85rem" },
                        }}
                      >
                        {task.title}
                      </Typography>
                      {isMobile &&
                        (Array.isArray(task.assignedToNames) &&
                        task.assignedToNames.length > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {task.assignedToNames.join(", ")}
                          </Typography>
                        ) : (
                          task.assignedToName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem" }}
                            >
                              {task.assignedToName}
                            </Typography>
                          )
                        ))}
                      {task.assignedByName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        >
                          Asignada por: {task.assignedByName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={() => onView(task)}>
                      <Chip
                        label={TASK_PRIORITY_LABELS[task.priority]}
                        size="small"
                        sx={{
                          backgroundColor: TASK_PRIORITY_COLORS[task.priority],
                          color: "white",
                          fontWeight: 600,
                          fontSize: { xs: "0.6rem", sm: "0.75rem" },
                        }}
                      />
                    </TableCell>
                    {!isMobile && (
                      <TableCell onClick={() => onView(task)}>
                        {Array.isArray(task.assignedToNames) &&
                        task.assignedToNames.length > 0
                          ? task.assignedToNames.join(", ")
                          : task.assignedToName || (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                Sin asignar
                              </Typography>
                            )}
                      </TableCell>
                    )}
                    <TableCell onClick={() => onView(task)}>
                      {task.dueDate ? (
                        <Typography
                          variant="body2"
                          color={isOverdue(task) ? "error" : "text.primary"}
                          fontWeight={isOverdue(task) ? 600 : 400}
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                        >
                          {new Date(task.dueDate).toLocaleString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {isOverdue(task) && " (Vencida)"}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                        >
                          Sin fecha
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={() => onView(task)}>
                      <Chip
                        label={TASK_STATUS_LABELS[task.status]}
                        size="small"
                        color={
                          task.status === TASK_STATUS.COMPLETED
                            ? "success"
                            : task.status === TASK_STATUS.IN_PROGRESS
                              ? "info"
                              : task.status === TASK_STATUS.CANCELLED
                                ? "default"
                                : "warning"
                        }
                        variant="outlined"
                        sx={{ fontSize: { xs: "0.6rem", sm: "0.75rem" } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(task);
                            }}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(task);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(task);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                { fontSize: { xs: "0.7rem", sm: "0.85rem" } },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await remove(deleteTarget.id);
          setDeleteTarget(null);
          onRefresh();
        }}
        title="Eliminar Tarea"
        message={`¿Eliminar tarea "${deleteTarget?.title}"?`}
        type="delete"
      />
    </Box>
  );
};

export default TaskList;
